import { collection, doc, setDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Check if user exists in main app users collection
 * @param {string} phoneNumber - User's phone number
 * @returns {Promise<boolean>} - true if user exists
 */
export const checkUserExists = async (phoneNumber) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('phone_number', '==', phoneNumber));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking user existence:', error);
    return false; // Assume new user on error
  }
};

/**
 * Check if user already registered for this event
 * @param {string} phoneNumber - User's phone number
 * @param {string} eventId - Event ID
 * @returns {Promise<boolean>} - true if already registered
 */
export const checkDuplicateRegistration = async (phoneNumber, eventId) => {
  try {
    const registrationsRef = collection(db, 'meetingRegistrations');
    const q = query(
      registrationsRef,
      where('phoneNumber', '==', phoneNumber),
      where('eventId', '==', eventId)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking duplicate registration:', error);
    return false;
  }
};

/**
 * @param {Object} data - Form data
 * @param {string} userId - Firebase Auth UID
 * @param {Object} eventConfig - Full event object from events.js
 */
export const saveRegistration = async (data, userId, eventConfig) => {
  try {
    const { phoneNumber, name, email, gender, isAbove18, disability, disabilityIsOther } = data;

    const isDuplicate = await checkDuplicateRegistration(phoneNumber, eventConfig.id);
    if (isDuplicate) {
      return { success: false, error: 'You have already registered for this event.', isDuplicate: true };
    }

    const isExistingUser = await checkUserExists(phoneNumber);
    const registrationRef = doc(collection(db, 'meetingRegistrations'));

    await setDoc(registrationRef, {
      userId,
      name,
      phoneNumber,
      email,
      gender,
      isAbove18,
      disability,
      disabilityIsOther,
      userStatus: isExistingUser ? 'existing' : 'new',
      eventId: eventConfig.id,
      source: eventConfig.registration.source,  // nested path
      timestamp: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error saving registration:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get total registration count for an event
 * @param {string} eventId - Event ID
 * @returns {Promise<number>} - Total registrations
 */
export const getRegistrationCount = async (eventId) => {
  try {
    const registrationsRef = collection(db, 'meetingRegistrations');
    const q = query(registrationsRef, where('eventId', '==', eventId));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting registration count:', error);
    return 0;
  }
};